(() => {
  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => `${cartRoot()}cart/add.js`.replace(/\/{2,}/g, '/');
  const cartJsonUrl = () => `${cartRoot()}cart.js`.replace(/\/{2,}/g, '/');
  const cartSectionUrl = () => `${cartRoot()}cart?sections=cart-drawer-section`.replace(/([^:]\/)\/+/g, '$1');

  const countLabel = (count) => (count > 9 ? '9+' : String(count));
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const activeFlights = new Set();

  document.addEventListener('hanfi:fly-to-cart:start', (event) => {
    const flight = event.target;
    if (!(flight instanceof HTMLElement)) return;

    let settle;
    const completion = new Promise((resolve) => {
      settle = resolve;
    });
    activeFlights.add(completion);

    let timeoutId;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeoutId);
      activeFlights.delete(completion);
      settle();
    };

    timeoutId = window.setTimeout(finish, 1000);
    flight.addEventListener('hanfi:fly-to-cart:complete', finish, { once: true });
  });

  const waitForActiveFlights = () => Promise.allSettled(Array.from(activeFlights));

  const updateCartBadges = async () => {
    try {
      const response = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart.item_count) || 0;
      document.querySelectorAll('[data-hanfi-cart-count], .hanfi-mobile-bottom-nav__count').forEach((badge) => {
        badge.textContent = countLabel(count);
        badge.hidden = count <= 0;
      });
    } catch (_) {}
  };

  const replaceCartDrawerSection = (html) => {
    const currentSection = document.getElementById('shopify-section-cart-drawer-section');
    if (!currentSection) return false;
    if (!html) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incomingSection = doc.getElementById('shopify-section-cart-drawer-section');
    if (!incomingSection) {
      const incomingDrawer = doc.querySelector('theme-drawer#cart-drawer, #cart-drawer');
      if (!incomingDrawer) return false;
      currentSection.innerHTML = doc.body.innerHTML;
      document.dispatchEvent(new CustomEvent('hanfi:cart-drawer-refreshed', { bubbles: true }));
      return true;
    }

    currentSection.innerHTML = incomingSection.innerHTML;
    document.dispatchEvent(new CustomEvent('hanfi:cart-drawer-refreshed', { bubbles: true }));
    return true;
  };

  const refreshCartDrawerSection = async (html) => {
    if (replaceCartDrawerSection(html)) return true;
    try {
      const response = await fetch(cartSectionUrl(), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) return false;

      const payload = await response.json();
      return replaceCartDrawerSection(payload?.['cart-drawer-section']);
    } catch (_) {
      return false;
    }
  };

  const refreshCartComponents = async () => {
    await refreshCartDrawerSection();
    await updateCartBadges();
  };

  const openCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    const themeDrawer = drawer?.closest('theme-drawer') || drawer;
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      themeDrawer.open();
      return;
    }

    const trigger = document.querySelector('[aria-controls="cart-drawer"], [on\\:click="#cart-drawer/toggle"]');
    if (trigger instanceof HTMLElement) {
      trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    }
  };

  const setCartPending = (pending) => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    drawer.classList.toggle('hanfi-cart-is-updating', pending);
    if (pending) {
      drawer.setAttribute('aria-busy', 'true');
    } else {
      drawer.removeAttribute('aria-busy');
    }
  };

  const closeCartDrawerForCheckout = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer?.hasAttribute('open')) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      const panel = drawer.querySelector('dialog[open]');
      panel?.close?.();
      drawer.removeAttribute('open');
    }

    document.documentElement.classList.remove('hanfi-cart-open');
  };

  const getCartIcon = () => {
    const candidates = Array.from(document.querySelectorAll(
      '.hanfi-header-pro__icon-button[aria-label="Cart"], [aria-controls="cart-drawer"], .hanfi-mobile-bottom-nav__item[aria-label*="Bag" i], .header-actions__cart-icon'
    ));
    return candidates.find((element) => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
    }) || null;
  };

  const getProductMedia = (form) => {
    const scope = form.closest('.shopify-section, product-card, .hanfi-product-card, .hanfi-shoppable-modal, body');
    return scope?.querySelector(
      '.hanfi-main-slide img, .hanfi-gallery-wrapper img, .product-media img, .product-media-gallery img, .product__media img, [data-product-media] img, img'
    );
  };

  const animateFlyToCart = (form, submitter) => {
    if (prefersReducedMotion()) return;
    const cartIcon = getCartIcon();
    const media = getProductMedia(form);
    const source = submitter || media || form;
    if (!cartIcon || !source) return;

    const image = media?.currentSrc || media?.src || '';
    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--main');
    if (image) flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--width', '76px');
    flyToCartElement.style.setProperty('--height', '76px');
    flyToCartElement.source = source;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  };

  const shouldHandleForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return false;
    return !form.closest('product-form-component');
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!shouldHandleForm(form)) return;

    event.preventDefault();
    setCartPending(true);
    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector('[type="submit"]');
    animateFlyToCart(form, submitter);
    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;

    try {
      const formData = new FormData(form);
      formData.set('sections', 'cart-drawer-section');
      formData.set('sections_url', window.location.pathname);
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart add failed');
      const payload = await response.json().catch(() => null);
      if (payload?.status) throw new Error(payload.message || 'Cart add failed');
      await refreshCartDrawerSection(payload?.sections?.['cart-drawer-section']);
      await waitForActiveFlights();
      openCartDrawer();
      updateCartBadges();
      setCartPending(false);
      document.dispatchEvent(new CustomEvent('hanfi:cart-added', { bubbles: true }));
    } catch (_) {
      form.submit();
    } finally {
      setCartPending(false);
      submitter?.removeAttribute('aria-busy');
      if (submitter) submitter.disabled = false;
    }
  }, true);

  document.addEventListener('cart:refresh', async () => {
    await refreshCartComponents();
    openCartDrawer();
  });

  document.addEventListener('hanfi:cart-add-start', () => {
    setCartPending(true);
  });

  document.addEventListener('hanfi:cart-add-finish', () => {
    setCartPending(false);
  });

  document.addEventListener('hanfi:cart-add-response', async (event) => {
    const html = event?.detail?.sections?.['cart-drawer-section'];
    if (html) replaceCartDrawerSection(html);
    updateCartBadges();
    setCartPending(false);
    await waitForActiveFlights();
    openCartDrawer();
    document.dispatchEvent(new CustomEvent('hanfi:cart-added', { bubbles: true }));
  });

  document.addEventListener('shopify:cart:lines-update', async (event) => {
    const promise = event?.promise;
    if (promise && typeof promise.then === 'function') {
      try {
        await promise;
      } catch (_) {}
    }
    if (event?.action === 'add' && event?.detail?.didError !== true) {
      await waitForActiveFlights();
      openCartDrawer();
      updateCartBadges();
      return;
    }
    await refreshCartComponents();
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id === 'cart-drawer') document.documentElement.classList.add('hanfi-cart-open');
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id === 'cart-drawer') document.documentElement.classList.remove('hanfi-cart-open');
  });

  document.addEventListener('click', (event) => {
    const applyButton = event.target instanceof Element ? event.target.closest('[data-hanfi-apply-offer]') : null;
    if (!(applyButton instanceof HTMLElement)) return;
    const code = applyButton.dataset.hanfiApplyOffer;
    if (!code) return;

    const input = document.getElementById('cart-discount');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = code;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    const form = input.closest('form');
    if (form?.requestSubmit) {
      form.requestSubmit();
    } else {
      form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const checkoutTrigger = target?.closest(
      '#checkout, .cart__checkout-button, button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [data-gokwik], [data-go-kwik], [id*="gokwik" i], [class*="gokwik" i]'
    );
    if (checkoutTrigger) closeCartDrawerForCheckout();
  }, true);

  if (document.querySelector('theme-drawer#cart-drawer[open]')) {
    document.documentElement.classList.add('hanfi-cart-open');
  }
})();
